/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Row, Col, Input, Button, Badge } from 'reactstrap';
import { DataTableColumn, ColumnFilter } from 'types/table.types';
import { ColumnSelector } from './ColumnSelector';

export interface TableHeaderProps<T> {
  columns: DataTableColumn<T>[];
  search: string;
  onSearchChange: (value: string) => void;
  filters: ColumnFilter[];
  onFilterChange: (filterId: string, value: any) => void;
  onRemoveFilter: (filterId: string) => void;
  onClearFilters: () => void;
  enableSearch?: boolean;
  enableFiltering?: boolean;
  searchPlaceholder?: string;
  // Column visibility and export
  enableColumnVisibility?: boolean;
  enableExport?: boolean;
  visibleColumns?: any[];
  onExport?: () => void;
  isLoading?: boolean;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void;
  onColumnToggle?: (columnId: string) => void;
  // Bulk actions
  enableBulkActions?: boolean;
  selectedRows?: string[];
  bulkActions?: any[];

  toolbarExtra?: React.ReactNode;
}

export const TableHeader = React.memo(
  React.forwardRef<HTMLDivElement, TableHeaderProps<any>>(
    (
      {
        columns,
        search,
        onSearchChange,
        filters,
        onFilterChange,
        onRemoveFilter,
        onClearFilters,
        enableSearch = true,
        enableFiltering = true,
        searchPlaceholder = 'Search...',
        enableColumnVisibility = false,
        enableExport = false,
        visibleColumns = [],
        onExport,
        isLoading = false,
        columnVisibility = {},
        onColumnVisibilityChange,
        onColumnToggle,
        enableBulkActions = false,
        selectedRows = [],
        bulkActions = [],
        toolbarExtra,
      },
      ref,
    ) => {
      const filterableColumns = useMemo(
        () => columns.filter((col) => col.enableFiltering),
        [columns],
      );

      const activeFilterCount = useMemo(() => filters.length, [filters]);

      return (
        <div ref={ref} className="mb-3">
          {/* Main Toolbar */}
          {(enableSearch || enableColumnVisibility || enableExport) && (
            <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between bg-white p-3">
              {/* Search Section - Left Side */}
              {enableSearch && (
                <div className="flex-grow-1" style={{ maxWidth: '420px' }}>
                  <div className="d-flex">
                    <Input
                      type="text"
                      placeholder={searchPlaceholder}
                      value={search}
                      onChange={(e) => onSearchChange(e.target.value)}
                      className="form-control shadow-none"
                      style={{ height: '42px' }}
                    />
                    {search && (
                      <Button
                        type="button"
                        color="light"
                        onClick={() => onSearchChange('')}
                        className="rounded-start-0 border-start-0 d-flex align-items-center justify-content-center"
                        style={{ width: '42px', height: '42px' }}
                      >
                        <i className="bx bx-x"></i>
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Right Side Controls */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {toolbarExtra}

                {enableColumnVisibility && (
                  <ColumnSelector
                    columns={columns}
                    visibility={columnVisibility}
                    onVisibilityChange={onColumnVisibilityChange!}
                    onColumnToggle={onColumnToggle!}
                  />
                )}

                {enableExport && (
                  <Button
                    size="md"
                    color="info"
                    outline
                    onClick={onExport}
                    disabled={isLoading}
                    className="d-flex align-items-center gap-2"
                  >
                    <i className="ri-download-2-line"></i> Export
                  </Button>
                )}

                {enableFiltering && filterableColumns.length > 0 && activeFilterCount > 0 && (
                  <>
                    <Badge color="info" className="px-3 py-2 d-flex align-items-center gap-1">
                      {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
                    </Badge>
                    <Button size="sm" color="light" outline onClick={onClearFilters}>
                      Clear Filters
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Bulk Actions Toolbar - Shown only when rows are selected */}
          {enableBulkActions && selectedRows.length > 0 && (
            <div className="mt-3 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded p-3 d-flex align-items-center gap-3 shadow-sm">
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <Badge color="primary" pill className="px-3 py-1">
                  {selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected
                </Badge>
                <span className="text-muted small">Choose an action below</span>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {bulkActions.map((action) => (
                  <Button
                    key={action.id}
                    size="sm"
                    color={action.color || 'primary'}
                    onClick={() => action.onClick(selectedRows)}
                    disabled={isLoading}
                    className="d-flex align-items-center gap-2"
                  >
                    {action.icon} {action.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Active Filters Display (below toolbar) */}
          {enableFiltering && filters.length > 0 && (
            <div className="mt-3">
              <div className="active-filters d-flex flex-wrap gap-2">
                {filters.map((filter) => (
                  <Badge
                    key={filter.id}
                    color="secondary"
                    pill
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <span className="fw-medium">{filter.id}:</span>
                    <code className="small text-white bg-secondary bg-opacity-75 px-2 py-0.5 rounded">
                      {String(filter.value)}
                    </code>
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ width: '14px', height: '14px', fontSize: '0.75rem', opacity: 0.9 }}
                      onClick={() => onRemoveFilter(filter.id)}
                      aria-label="Remove filter"
                    />
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
  ),
);

TableHeader.displayName = 'TableHeader';
